import Colors from '../../layout/theme-next/colors';

type DiffTokenType = 'tag' | 'text';
interface DiffToken { type: DiffTokenType, value: string }
type DiffOpType = 'equal' | 'add' | 'remove';

// Matches an HTML tag, a run of non-space/non-tag chars (a word), or a run of whitespace.
const TOKEN_REGEX = /<[^>]+>|[^\s<]+|\s+/g;

const tokenize = (html: string): DiffToken[] =>
  (html.match(TOKEN_REGEX) ?? []).map((value): DiffToken => ({
    type: value.startsWith('<') ? 'tag' : 'text',
    value,
  }));

// Classic LCS-based diff. O(n*m); fine for step-sized text, guarded by MAX_TOKENS in htmlWordDiff.
const diffTokens = (oldTokens: DiffToken[], newTokens: DiffToken[]) => {
  const n = oldTokens.length;
  const m = newTokens.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = oldTokens[i].value === newTokens[j].value
        ? lcs[i + 1][j + 1] + 1
        : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const ops: Array<{ type: DiffOpType, token: DiffToken }> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (oldTokens[i].value === newTokens[j].value) {
      ops.push({ type: 'equal', token: oldTokens[i] });
      i++; j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      ops.push({ type: 'remove', token: oldTokens[i] });
      i++;
    } else {
      ops.push({ type: 'add', token: newTokens[j] });
      j++;
    }
  }
  while (i < n) { ops.push({ type: 'remove', token: oldTokens[i] }); i++; }
  while (j < m) { ops.push({ type: 'add', token: newTokens[j] }); j++; }
  return ops;
};

const ADD_STYLE = `background-color:${Colors.successSoft}`;
const REMOVE_STYLE = `background-color:${Colors.errorSoft};color:${Colors.errorDark};text-decoration:line-through`;
const MAX_TOKENS = 4000;

// Renders inline word-level track-changes markup (added text highlighted, removed text
// struck through) between two HTML strings, keeping structural tags/whitespace unhighlighted.
// ponytail: assumes old/new share mostly the same tag structure (true for rich-text step
// edits); a tag only present on one side is dropped/kept as-is rather than diffed structurally.
export const htmlWordDiff = (oldHtml?: string, newHtml?: string): string => {
  const oldStr = oldHtml ?? '';
  const newStr = newHtml ?? '';
  if (oldStr === newStr) { return newStr; }

  const oldTokens = tokenize(oldStr);
  const newTokens = tokenize(newStr);
  if (oldTokens.length > MAX_TOKENS || newTokens.length > MAX_TOKENS) { return newStr; }

  return diffTokens(oldTokens, newTokens).map(({ type, token }) => {
    if (token.type === 'tag') { return type === 'remove' ? '' : token.value; }
    if (!token.value.trim()) { return token.value; }
    if (type === 'add') { return `<span style="${ADD_STYLE}">${token.value}</span>`; }
    if (type === 'remove') { return `<span style="${REMOVE_STYLE}">${token.value}</span>`; }
    return token.value;
  }).join('');
};
